import {
  type BlockComponent,
  type EditorHost,
} from '@blocksuite/affine/block-std';
import {
  type AffineInlineEditor,
  getSelectedInlineEditors,
} from '@blocksuite/affine/blocks';
import { WithDisposable } from '@blocksuite/global/utils';
import type { InlineRange } from '@blocksuite/inline';
import { type BlockModel } from '@blocksuite/store';
import { html, LitElement, type PropertyValues } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';

export class DocnboardChatPanel extends WithDisposable(LitElement) {
  updatePosition = (position: { x: string; y: string; height: number }) => {
    this._position = position;
  };

  constructor() {
    super();
  }

  private setSelection(inlineEditor: AffineInlineEditor, range: InlineRange) {
    const domRange = inlineEditor.toDomRange(range);
    if (!domRange) {
      return;
    }

    const domRects = domRange.getClientRects();
    Object.values(domRects).forEach(domRect => {
      if (!this.mockSelectionContainer) {
        return;
      }
      const mockSelection = document.createElement('div');
      mockSelection.classList.add('mock-selection');
      mockSelection.style.position = 'absolute';
      mockSelection.style.backgroundColor = 'rgba(35, 131, 226, 0.28)';
      mockSelection.style.left = `${domRect.left}px`;
      mockSelection.style.top = `${domRect.top}px`;
      mockSelection.style.width = `${domRect.width}px`;
      mockSelection.style.height = `${domRect.height}px`;

      this.mockSelectionContainer.append(mockSelection);
    });
  }

  override connectedCallback() {
    super.connectedCallback();
  }

  override createRenderRoot() {
    return this;
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
  }

  protected override firstUpdated(_changedProperties: PropertyValues): void {
    super.firstUpdated(_changedProperties);

    const inlineEditors = getSelectedInlineEditors(
      this.selectedElements,
      inlineRoot => inlineRoot.inlineEditor
    );
    inlineEditors.forEach(inlineEditor => {
      const range = inlineEditor.getInlineRange();
      if (!range) return;
      this.setSelection(inlineEditor, range);
    });

    this.onMounted();
  }

  override render() {
    const chatPanelAIStyles = this._position
      ? {
          transform: `translate(${this._position.x}, ${this._position.y})`,
          maxHeight: `${Math.min(this._position.height, 344)}px`,
          position: 'fixed',
          top: 0,
          left: 0,
          width: '370px',
          zIndex: '50',
          pointerEvents: 'auto',
        }
      : {
          visibility: 'hidden',
        };

    return html`${html` <div
        style="${styleMap({
          pointerEvents: 'auto',
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          zIndex: '49',
        })}"
        @click="${() => this.abortController.abort()}"
      ></div>`}<docnboard-chat-panel-ai
        .model=${this.model}
        .host=${this.host}
        .abortController=${this.abortController}
        style=${styleMap(chatPanelAIStyles)}
      >
      </docnboard-chat-panel-ai>
      <div class="docnboard-chat-panel-mock-selection-container"></div>`;
  }

  @state()
  private accessor _position: {
    x: string;
    y: string;
    height: number;
  } | null = null;

  @property({ attribute: false })
  accessor abortController!: AbortController;

  @query('docnboard-chat-panel-ai')
  accessor chatPanelAI!: HTMLElement;

  @property({ attribute: false })
  accessor host!: EditorHost;

  @query('.docnboard-chat-panel-mock-selection-container')
  accessor mockSelectionContainer!: HTMLDivElement;

  @property({ attribute: false })
  accessor model!: BlockModel;

  accessor onMounted!: () => void;

  @property({ attribute: false })
  accessor selectedElements!: BlockComponent[];
}

declare global {
  interface HTMLElementTagNameMap {
    'docnboard-chat-panel': DocnboardChatPanel;
  }
}
